import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useDatasetNames } from '@/features/catalog-browser';
import {
  ACTIVATE_POLICY_PERMISSION,
  AUTHOR_POLICY_PERMISSION,
  CONFIGURE_POLICY_ENFORCEMENT_PERMISSION,
  DELETE_POLICY_PERMISSION,
  DEPRECATE_POLICY_PERMISSION,
  PUBLISH_POLICY_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import type { PolicyRule } from '@/shared/api/generated/policy';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { Tabs } from '@/shared/ui/Tabs';

import {
  useAbacPosture,
  useActivations,
  useActiveBundles,
  useBundleVersions,
  useDraftPolicy,
  useEnforcement,
  usePolicyBundles,
  usePolicyVocabulary,
  useVersionPolicy,
} from '../api/policy';
import { enforcementSummary, runningHashesOf } from '../model/bundles';
import { analysable, differsFrom } from '../model/workspace';
import type { EditorState } from '../model/workspace';
import { CheckAccessPanel } from './CheckAccessPanel';
import { OtherBundles } from './OtherBundles';
import { PolicyHero } from './PolicyHero';
import { PublishDialog } from './PublishDialog';
import { RulesPanel } from './RulesPanel';
import type { EditorBase } from './RulesPanel';
import { VersionsPanel } from './VersionsPanel';

/* Access policy, organised around what people come here to do: see what decides access, edit the
 * rules, check a request, and publish or roll back.
 *
 * **Only one bundle decides access** — the one under the name the platform consults — so the page is
 * about that bundle, and any other is listed at the bottom as stored and consulted by nothing.
 *
 * **There is no step that creates it.** The editor is always there; the first save creates the
 * bundle. It is deliberately *not* created up front: a governing bundle with nothing published makes
 * "Roles + policy" refuse every request, where no bundle at all leaves roles deciding — so an empty
 * bundle made by default would turn switching policy on into a lockout.
 */

type Tab = 'rules' | 'versions' | 'check';

const NO_RULES: readonly PolicyRule[] = [];

export function PolicyBundlesScreen(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const enforcement = useEnforcement();
  const posture = useAbacPosture();
  const active = useActiveBundles();
  const bundles = usePolicyBundles();
  const vocabulary = usePolicyVocabulary();
  const datasets = useDatasetNames();

  const [tab, setTab] = useState<Tab>('rules');
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [justSaved, setJustSaved] = useState<{
    readonly rules: readonly PolicyRule[];
    readonly at: number;
  } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const governingName =
    enforcement.data?.bundle_name ?? active.data?.authzBundleName ?? 'authz';
  const governing = bundles.data?.find((bundle) => bundle.name === governingName) ?? null;
  const governingId = governing?.id ?? null;
  const versions = useBundleVersions(governingId);
  const activations = useActivations(governingId);
  const draft = versions.data?.find((version) => version.state === 'draft') ?? null;
  const activeVersion =
    versions.data?.find((version) => version.state === 'active') ?? null;
  const activeSemver = activeVersion?.semver ?? null;
  const draftPolicy = useDraftPolicy(governingId, draft !== null);
  const activePolicy = useVersionPolicy(governingId, activeSemver);

  /* The saved rules stand in for the draft until the draft has been read back, so the editor does
     not fall back to the active version's rules for the moment in between. */
  useEffect(() => {
    if (justSaved !== null && draftPolicy.dataUpdatedAt > justSaved.at)
      setJustSaved(null);
  }, [justSaved, draftPolicy.dataUpdatedAt]);

  const base = useMemo<EditorBase | 'loading' | 'failed'>(() => {
    if (justSaved !== null) {
      return {
        source: 'draft',
        rules: justSaved.rules,
        draft,
        activeSemver,
        uploadedDraft: false,
      };
    }
    if (governing === null) {
      return {
        source: 'new',
        rules: NO_RULES,
        draft: null,
        activeSemver: null,
        uploadedDraft: false,
      };
    }
    if (versions.isError) return 'failed';
    if (versions.data === undefined) return 'loading';
    if (draft !== null) {
      if (draftPolicy.isError) return 'failed';
      if (draftPolicy.data === undefined) return 'loading';
      return {
        source: 'draft',
        rules: draftPolicy.data.rules ?? NO_RULES,
        draft,
        activeSemver,
        uploadedDraft: draftPolicy.data.rules === null,
      };
    }
    if (activeSemver !== null) {
      if (activePolicy.isError) return 'failed';
      if (activePolicy.data === undefined) return 'loading';
      return {
        source: 'active',
        rules: activePolicy.data.rules ?? NO_RULES,
        draft: null,
        activeSemver,
        uploadedDraft: false,
      };
    }
    return {
      source: 'new',
      rules: NO_RULES,
      draft: null,
      activeSemver: null,
      uploadedDraft: false,
    };
  }, [
    justSaved,
    governing,
    versions.isError,
    versions.data,
    draft,
    draftPolicy.isError,
    draftPolicy.data,
    activeSemver,
    activePolicy.isError,
    activePolicy.data,
  ]);

  const may = (permission: string): boolean => can(identity.data, permission);
  const runningHashes = runningHashesOf(active.data?.items ?? []);
  const policyOn = posture.data?.enabled ?? enforcement.data?.abac_enabled ?? false;
  const reading =
    enforcement.data === undefined ? null : enforcementSummary(enforcement.data).reading;
  const unsaved =
    editor !== null && typeof base === 'object' && differsFrom(editor.rows, base.rules)
      ? analysable(editor)
      : null;
  const ruleCount =
    editor?.rows.length ?? (typeof base === 'object' ? base.rules.length : undefined);
  const versionCount = versions.data?.filter((version) => version.semver).length;
  const others = (bundles.data ?? []).filter((bundle) => bundle.name !== governingName);

  const header = (
    <PageHeader
      title={t('policy.title')}
      help={t('help.feature.policy')}
      lede={t('policy.lede')}
    />
  );

  if (enforcement.isPending || bundles.isPending) {
    return (
      <div className="lq-pq">
        {header}
        <div className="lq-pq__loading" role="status" aria-label={t('policy.loading')} />
      </div>
    );
  }

  return (
    <div className="lq-pq">
      {header}

      {flash !== null ? (
        <p className="lq-pq__flash" role="status">
          <span className="lq-pq__flashText">{flash}</span>
          <button
            type="button"
            className="lq-pq__dismiss"
            aria-label={t('policy.flash.dismiss')}
            onClick={() => {
              setFlash(null);
            }}
          >
            ×
          </button>
        </p>
      ) : null}

      {enforcement.data !== undefined ? (
        <PolicyHero
          enforcement={enforcement.data}
          posture={posture.data}
          postureFailed={posture.isError}
          governingName={governingName}
          hasGoverning={governing !== null}
          activeSemver={activeSemver}
          activeHash={activeVersion?.content_hash ?? null}
          maxStalenessSeconds={active.data?.maxStalenessSeconds ?? 60}
          canConfigure={may(CONFIGURE_POLICY_ENFORCEMENT_PERMISSION)}
          onOpenVersions={() => {
            setTab('versions');
          }}
          onFlash={setFlash}
        />
      ) : (
        <Alert tone="error" title={t('policy.hero.failed')}>
          {enforcement.error.message}
        </Alert>
      )}

      {bundles.isError ? (
        <Alert tone="error" title={t('policy.bundles.failed')}>
          {bundles.error.message}
        </Alert>
      ) : null}

      <Tabs<Tab>
        label={t('policy.tabs.label')}
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'rules', label: t('policy.tabs.rules'), count: ruleCount },
          { id: 'versions', label: t('policy.tabs.versions'), count: versionCount },
          { id: 'check', label: t('policy.tabs.check') },
        ]}
      />

      {tab === 'rules' ? (
        <div role="tabpanel" aria-label={t('policy.tabs.rules')}>
          {base === 'loading' ? (
            <SkeletonRows rows={4} columns={2} />
          ) : base === 'failed' ? (
            <Alert tone="error" title={t('policy.rules.readFailed')}>
              {t('policy.rules.readFailedBody')}
            </Alert>
          ) : (
            <RulesPanel
              governing={governing}
              governingName={governingName}
              base={base}
              editor={editor}
              onEditor={setEditor}
              vocabulary={vocabulary.data}
              vocabularyFailed={vocabulary.isError}
              datasetNames={datasets.data?.byId}
              policyOff={!policyOn}
              canAuthor={may(AUTHOR_POLICY_PERMISSION)}
              canPublish={may(PUBLISH_POLICY_PERMISSION)}
              hasDraft={draft !== null || justSaved !== null}
              onSaved={(_bundleId, rules) => {
                setJustSaved({ rules, at: Date.now() });
                setFlash(t('policy.rules.savedFlash'));
              }}
              onReviewPublish={() => {
                setPublishing(true);
              }}
            />
          )}
        </div>
      ) : null}

      {tab === 'versions' && governing === null ? (
        <div role="tabpanel" aria-label={t('policy.tabs.versions')}>
          <p className="lq-pq__emptyRules">{t('policy.versions.none')}</p>
        </div>
      ) : null}

      {tab === 'versions' && governing !== null ? (
        <div role="tabpanel" aria-label={t('policy.tabs.versions')}>
          <VersionsPanel
            governing={governing}
            versions={versions.data}
            versionsFailed={versions.isError}
            activations={activations.data}
            activationsFailed={activations.isError}
            runningHashes={runningHashes}
            runtimeAvailable={enforcement.data?.runtime_available ?? false}
            policyOn={policyOn}
            canPublish={may(PUBLISH_POLICY_PERMISSION)}
            canActivate={may(ACTIVATE_POLICY_PERMISSION)}
            canDeprecate={may(DEPRECATE_POLICY_PERMISSION)}
            datasetNames={datasets.data?.byId}
            onReviewPublish={() => {
              setPublishing(true);
            }}
            onEditRules={() => {
              setTab('rules');
            }}
            onFlash={setFlash}
          />
        </div>
      ) : null}

      {tab === 'check' && reading !== null ? (
        <div role="tabpanel" aria-label={t('policy.tabs.check')}>
          <CheckAccessPanel
            vocabulary={vocabulary.data}
            datasetNames={datasets.data?.byId}
            reading={reading}
            activeSemver={activeSemver}
            activeRules={activeSemver === null ? NO_RULES : activePolicy.data?.rules}
            draftRules={draft === null ? undefined : draftPolicy.data?.rules}
            hasDraft={draft !== null}
            unsaved={unsaved}
            policyOff={!policyOn}
          />
        </div>
      ) : null}

      <OtherBundles
        bundles={others}
        canDelete={may(DELETE_POLICY_PERMISSION)}
        onFlash={setFlash}
      />

      <PublishDialog
        open={publishing}
        bundle={governing}
        versions={versions.data ?? []}
        draftRules={draft === null ? undefined : draftPolicy.data?.rules}
        activeRules={activeSemver === null ? NO_RULES : activePolicy.data?.rules}
        activeSemver={activeSemver}
        policyOff={!policyOn}
        datasetNames={datasets.data?.byId}
        onClose={() => {
          setPublishing(false);
        }}
        onPublished={(semver) => {
          setPublishing(false);
          setTab('versions');
          setFlash(t('policy.publish.done', { semver }));
        }}
      />
    </div>
  );
}
